import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarIcon, UsersIcon, BookmarkIcon, ArrowRightIcon, CopyIcon } from "./icons";
import { formatDate } from "./utils";
import { TRIP_NOT_STARTED_MESSAGE, hasTripStarted, isBookedTrip } from "./tripStatus";
import { completeTrip } from "../../services/api";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { CommunityError } from "../../errors/sentryErrors";
const ItineraryCard = ({ trip, onViewDetails, onTripUpdated }) => {
  const navigate = useNavigate();
  const [isMarkingDone, setIsMarkingDone] = useState(false);
  // Read-only now: publishing happens as part of submitting the review, so this
  // only ever reports the moderation status the server already decided.
  const publishStatus = trip.communityPost?.status || null;
  const totalTravelers = (trip.adults || 1) + (trip.children || 0) + (trip.infants || 0);
  const travelerText = totalTravelers === 1 ? "Solo Trip" : `${totalTravelers} Travelers`;
  const isUpcoming = new Date(trip.startDate) > new Date();

  const image = trip.itinerary?.destinationImages?.[0]?.url ||
    `https://source.unsplash.com/800x600/?${encodeURIComponent(trip.destination)},travel`;

  const getBudgetDisplay = () => {
    if (trip.budget) {
      return `${trip.currency || 'INR'} ${trip.budget.toLocaleString()}`;
    }
    const estimatedCost = trip.itinerary?.costSummary?.estimatedTotal ||
                          trip.itinerary?.tripSummary?.budget?.amount;
    if (estimatedCost) {
      const currency = trip.currency || trip.itinerary?.tripSummary?.budget?.currency || 'INR';
      if (typeof estimatedCost === 'string') return estimatedCost;
      return `${currency} ${estimatedCost.toLocaleString()}`;
    }
    return null;
  };

  const budgetDisplay = getBudgetDisplay();

  // Matches the dashboard's bucketing: a trip is ongoing from its start date
  // through the end of its last day.
  const isOngoing = (() => {
    const today = new Date(new Date().setHours(0, 0, 0, 0)).getTime();
    const dayOf = (value) => new Date(new Date(value).setHours(0, 0, 0, 0)).getTime();
    return dayOf(trip.startDate) <= today && dayOf(trip.endDate) >= today;
  })();

  const isBooked = isBookedTrip(trip);
  // The API needs a trip to be marked completed before a review can be saved or published, booked or not, and nothing more:
  // a trip marked done from its start date can be reviewed at once, even while it is still going on.
  const canShowReview = trip.status === "completed";
  // Hidden until the start date: before that the API refuses it (409 TRIP_NOT_STARTED).
  const canMarkDone = trip.status !== "completed" && hasTripStarted(trip);

  // One badge, not two: this used to be a "Write/Continue/✓ Reviewed" button
  // stacked over a separate publish-status pill reporting the same underlying
  // moderation state from a different angle (reviewStatus vs the community
  // post's status). Collapsing them removes the chance of the two disagreeing
  // and halves the vertical space the card spends on it.
  const reviewBadge = (() => {
    if (publishStatus === "rejected") {
      return { label: "❌ Rejected", className: "bg-red-50 text-red-700" };
    }
    if (
      publishStatus === "approved" ||
      trip.reviewStatus === "approved" ||
      trip.reviewStatus === "published"
    ) {
      return { label: "✓ Approved", className: "bg-green-50 text-green-700" };
    }
    if (publishStatus === "pending" || trip.reviewStatus === "submitted") {
      return { label: "⏳ Pending Approval", className: "bg-yellow-50 text-yellow-700" };
    }
    if (trip.reviewStatus === "draft") {
      return { label: "Continue Review", className: "bg-white/95 text-gray-700 hover:bg-white" };
    }
    return { label: "Write Review", className: "bg-white/95 text-gray-700 hover:bg-white" };
  })();

  const handleMarkAsDone = async (e) => {
    e.stopPropagation();
    try {
      setIsMarkingDone(true);
      await completeTrip(trip.id);
      if (onTripUpdated) onTripUpdated();
    } catch (err) {
      Sentry.withScope((scope) => {
        scope.setTag("feature", "community");
        scope.setTag("app", "web");
        scope.setTag("error_source", err instanceof TypeError ? "network" : "server");
        Sentry.captureException(new CommunityError(err instanceof Error ? err.message : String(err)));
      });
      toast.error(
        err.code === "TRIP_NOT_STARTED"
          ? TRIP_NOT_STARTED_MESSAGE
          : err.message || "Failed to mark trip as done. Please try again."
      );
    } finally {
      setIsMarkingDone(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-md transition-shadow cursor-pointer flex flex-col h-full">
      <div className="relative h-40 sm:h-48" onClick={() => onViewDetails(trip)}>
        <img
          src={image}
          alt={trip.destination}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

        {canShowReview ? null : isUpcoming ? (
          <span className="absolute top-3 right-3 px-3 py-1 bg-orange-500 text-white text-xs font-semibold rounded-full">
            Upcoming
          </span>
        ) : isOngoing ? (
          <span className="absolute top-3 right-3 px-3 py-1 bg-teal-600 text-white text-xs font-semibold rounded-full flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse-soft" />
            Ongoing
          </span>
        ) : null}

        {isBooked && (
          <span className="absolute bottom-12 left-4 px-2.5 py-1 bg-teal-600 text-white text-[11px] font-semibold rounded-full shadow-sm">
            Booked with RoamMyWay
          </span>
        )}

        {/* Credits the traveller this itinerary came from, and links back to the
            post so the original is one click away rather than lost on copy. */}
        {trip.copiedFrom && (
          <span
            onClick={(e) => {
              if (!trip.copiedFrom.postSlug) return;
              e.stopPropagation();
              navigate(`/community/${trip.copiedFrom.postSlug}`);
            }}
            title={
              trip.copiedFrom.authorName
                ? `Copied from ${trip.copiedFrom.authorName}'s trip`
                : "Copied from a community trip"
            }
            className={`absolute top-3 left-3 px-2.5 py-1 bg-white/95 text-gray-700 text-[11px] font-semibold rounded-full flex items-center gap-1.5 shadow-sm ${
              trip.copiedFrom.postSlug ? "cursor-pointer hover:bg-white" : ""
            }`}
          >
            <CopyIcon />
            {trip.copiedFrom.authorName
              ? `From ${trip.copiedFrom.authorName.split(" ")[0]}`
              : "Copied"}
          </span>
        )}

        {/* Top-right. A trip marked done while it is still going on is both Ongoing and reviewable, so the Ongoing/Upcoming
            badge above is left out whenever this shows: they share the corner and the review is the one to act on. */}
        {canShowReview && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/reviews/${trip.id}/write`);
            }}
            className={`absolute top-3 right-3 px-2.5 py-1 text-[11px] font-semibold rounded-full shadow-sm transition-colors cursor-pointer ${reviewBadge.className}`}
          >
            {reviewBadge.label}
          </button>
        )}

        <h3 className="absolute bottom-3 left-4 text-white font-semibold text-lg">
          {trip.itinerary?.tripSummary?.tripTitle || `Trip to ${trip.destination}`}
        </h3>
      </div>

      <div className="p-4 flex-1 flex flex-col" onClick={() => onViewDetails(trip)}>
        <div className="flex items-center text-gray-500 text-sm mb-2">
          <CalendarIcon />
          <span className="ml-2">{formatDate(trip.startDate)} - {formatDate(trip.endDate)}</span>
        </div>

        <div className="flex items-center text-gray-500 text-sm mb-4">
          <UsersIcon />
          <span className="ml-2">{travelerText}</span>
        </div>

        <div className="flex items-center justify-between mt-auto">
          <div className="flex items-center gap-2">
            <span className="text-teal-600">
              <BookmarkIcon />
            </span>
            {budgetDisplay && (
              <span className="text-sm text-gray-500">
                {budgetDisplay}
              </span>
            )}
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onViewDetails(trip);
            }}
            className="text-teal-600 hover:text-teal-700 font-medium text-sm flex items-center cursor-pointer"
          >
            View Details
            <ArrowRightIcon />
          </button>
        </div>
      </div>

      {/* Past trips not yet marked complete still need this action; completed
          trips' review/publish status now lives in the single top-left badge
          on the image instead of down here. */}
      {canMarkDone && (
        <div className="px-4 pb-4 pt-2 border-t border-gray-50 mt-auto">
          <button
            onClick={handleMarkAsDone}
            disabled={isMarkingDone}
            className="w-full py-2 bg-teal-600 text-white text-sm font-semibold rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50"
          >
            {isMarkingDone ? "Marking as Done..." : "Mark as Done"}
          </button>
        </div>
      )}

    </div>
  );
};

export default ItineraryCard;
